import { connect, ConnectedProps } from 'react-redux'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../utils/hoc/withLoading'
import { ComponentTabObject } from '../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../utils/reducers'
import { CostBuyPayload } from '../../../../../../../../../../utils/reducers/company/costsBuy'
import { BuyTagsPayload } from '../../../../../../../../../../utils/reducers/buys/buyTags'
import BuyReadOnlyHeaderFinancialRows from '../../../../../header/BuyReadOnlyHeaderFinancialRows'
import BuyReadOnlyHeaderCosts from '../../../../../header/BuyReadOnlyHeaderCosts'
import BuyRealOnlyHeaderAccounts from '../../../../../header/BuyReadOnlyHeaderAccounts'
import useBuyInvoiceHeaderURLs from '../../../../../../../../../../utils/hooks/buys/orders/useBuyInvoiceHeaderURLs'
import BuyReadOnlyHeaderShipping from '../../../../../header/BuyReadOnlyHeaderShipping'
import BuyReadOnlyHeaderMain from '../../../../../header/BuyReadOnlyHeaderMain'
import BuyReadOnlyHeaderVariables from '../../../../../header/BuyReadOnlyHeaderVariables'
import { SellVariablePayload } from '../../../../../../../../../../utils/reducers/company/sellVariables'
import AppStyleDecider from '../../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { parseAddressToOneLine } from '../../../../../../../../../../utils/globals/address'
import { displayDate } from '../../../../../../../../../../utils/globals/date'
import { ITHeaderBuyOrderTagsNew } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../utils/globals/tailwind'
import { InputWithLabelMargin } from '../../../../../../../../../../forms/Input'
import { FormElement, FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import { getActiveCurrenciesDictionary } from '../../../../../../../../../../utils/reducers/getters'
import { useOrderITHeaderHandleChangeLogic } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/useOrderITHeaderHandleChange'
import { BuyInvoiceModelType } from '@teinor/erp/types/company/order/buy/buyInvoice'
import { ProductTypesPayload } from '../../../../../../../../../../utils/reducers/products/productTypes'

type BuyInvoiceFinishedStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxBuyInvoiceFinishedStepITHeader & {}

const BuyInvoiceFinishedStepITHeader: React.FC<BuyInvoiceFinishedStepITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={BuyInvoiceFinishedStepITHeaderDefault} NewComponent={BuyInvoiceFinishedStepITHeaderNew} {...rest} />
}

const BuyInvoiceFinishedStepITHeaderDefault: React.FC<BuyInvoiceFinishedStepITHeaderProps> = ({ buyTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useBuyInvoiceHeaderURLs()
    const tabs: ComponentTabObject = {
        'main': {
            component: BuyReadOnlyHeaderMain,
            text: t('dataInvoice', 'Datos de la factura'),
            extraProps: { orderHeaderURLs, buyTags }
        },
        'financialRow': {
            component: BuyReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'buy.invoice',
            extraProps: { orderHeaderURLs }
        },
        'costs': {
            component: BuyReadOnlyHeaderCosts,
            text: t('costsInvoice', 'Gastos de la factura'),
            permission: 'buy.invoice',
            extraProps: { orderHeaderURLs }
        },
        'shipping': {
            component: BuyReadOnlyHeaderShipping,
            text: t('shipping', 'Transporte'),
            permission: 'buy.invoice',
            extraProps: {
                orderHeaderURLs
            }
        },
        'accounts': {
            component: BuyRealOnlyHeaderAccounts,
            text: t('divideIntoChapters', 'Dividir en capítulos'),
            permission: 'buy.invoice',
            extraProps: { orderHeaderURLs }
        },
        'variables': {
            component: BuyReadOnlyHeaderVariables,
            text: t('buyVariables', 'Variables de la compra'),
            permission: 'buy.invoice',
            extraProps: { orderHeaderURLs }
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const BuyInvoiceFinishedStepITHeaderNew: React.FC<BuyInvoiceFinishedStepITHeaderProps> = ({ headerData, buyTags, setCompleteData, updateDataURL, getCompleteData, ...rest }) => {
    const { t } = useTwinTranslation()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData, updateDataURL })
    const activeCurrencies = getActiveCurrenciesDictionary()
    const extraCustomer: FormElement<keyof BuyInvoiceModelType>[] = []
    if (headerData?.CustomerId) {
        extraCustomer.push(
            {
                name: 'CustomerId',
                component: 'Custom',
                render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} />,
                hideSectionByDefault: true
            },
        )
    }

    const sections: FormRender<BuyInvoiceModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData?.custom_name} className='custom_name_input_field' />
                    }
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateBuyOrder', 'Fecha de la compra')} value={value ? displayDate(new Date(value)) : ''} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderBuyOrderTagsNew getCompleteData={getCompleteData} selectedTags={extraProps.selectedTags} dictTags={extraProps.buyTags} headerData={headerData || {}} onChange={handleChange} {...rest} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} />)
                    },
                    extraProps: {
                        selectedTags: headerData?.tags,
                        buyTags
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedNumber', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'supplier_number_order',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierNumber', 'Número del proveedor')} value={value} />,
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierInvoiceS', 'Fact. proveedor')} value={value} />,
                },
                {
                    name: 'supplier_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateSupplier', 'Fecha del proveedor')} value={value ? displayDate(new Date(value)) : ''} />,
                },
                ...extraCustomer,
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('billedBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('buyedBy', 'Comprado por')} value={headerData?.WorkingCenter.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WarehouseId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('warehouse', 'Almacen')} value={headerData?.Warehouse?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'BillingAddressId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={parseAddressToOneLine(headerData?.BillingWorkingCenter)
                        } />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]

    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
    )
}

const BuyInvoiceFinishedStepITHeaderDispatch = {
    setCostsBuy: (payload: CostBuyPayload) => ({ type: 'CHANGE_COSTSBUY', payload }),
    setBuyTags: (payload: BuyTagsPayload) => ({ type: 'CHANGE_BUY_TAGS', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
    setProductTypes: (payload: ProductTypesPayload) => ({ type: 'CHANGE_PRODUCT_TYPES', payload }),
}

export type ReduxBuyInvoiceFinishedStepITHeader = ConnectedProps<typeof buyInvoiceFinishedStepITHeaderConnect>
const mapBuyInvoiceFinishedStepITHeaderConnector = (state: AllReduxPayloads) => ({ costsBuy: state.costsBuy, buyTags: state.buyTags, sellVariables: state.sellVariables, productTypes: state.productTypes })
const buyInvoiceFinishedStepITHeaderConnect = connect(mapBuyInvoiceFinishedStepITHeaderConnector, BuyInvoiceFinishedStepITHeaderDispatch)
const BuyInvoiceFinishedStepITHeaderConnectLoading = withLoading(BuyInvoiceFinishedStepITHeader, [{ fetchUrl: '/api/app/config/cost/costBuy/getAllCostBuys', propName: 'costsBuy', setFunctionName: 'setCostsBuy' }, { fetchUrl: '/api/app/order/buy/tag/getAllBuyTags', propName: 'buyTags', setFunctionName: 'setBuyTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }, { fetchUrl: '/api/app/product/productType/getAllProductTypes', propName: 'productTypes', setFunctionName: 'setProductTypes' }])
const BuyInvoiceFinishedStepITHeaderConnect = buyInvoiceFinishedStepITHeaderConnect(BuyInvoiceFinishedStepITHeaderConnectLoading)

export default BuyInvoiceFinishedStepITHeaderConnect